import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { compose } from 'redux';
import { connect } from 'react-redux';

import { Collapsible, NoItemsPanel, TableLoader, BasicModal, DefaultButton } from 'common/components';

import noPaymentsImage from 'common/images/noPayments.svg';
import { FormattedMessage } from 'react-intl';
import { CheckIcon, CopyIcon } from 'common/icons';
import { withTheme } from 'common/styling/theme';
import { getDepositMetadata, getWithdrawalMetadata } from '../../payments/_redux';
import { staticStyles, getDynamicStyles } from './style';

const renderValue = (value, level = 0) => {
  if (value === null) {
    return '-';
  }

  if (typeof value !== 'object') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return (
      <div className={`PaymentMetadataModal__array PaymentMetadataModal__level-${level}`}>
        {value.map((item, index) => (
          <div key={index}>{renderValue(item, level + 1)}</div>
        ))}
      </div>
    );
  }

  return (
    <div className={`PaymentMetadataModal__object PaymentMetadataModal__level-${level}`}>
      {Object.entries(value).map(([key, val]) => (
        <div
          key={key}
          className={`PaymentMetadataModal__row ${level === 0 ? 'PaymentMetadataModal__row--root' : ''} ${
            typeof val === 'object' && val !== null ? 'PaymentMetadataModal__row--nested' : ''
          }`}>
          <div className="PaymentMetadataModal__key">{key}</div>
          <div className="PaymentMetadataModal__value">{renderValue(val, level + 1)}</div>
        </div>
      ))}
    </div>
  );
};

const formatMetadataDate = value => {
  const normalized = value.replace(' ', 'T').replace(/(\.\d{3})\d+/, '$1');

  const date = new Date(normalized);

  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).format(date);

  // 10:54:46 AM -> ["10:54", ":46", "AM"]
  const match = time.match(/^(.+?)(:\d{2})\s(AM|PM)$/);

  return {
    hoursMinutes: match?.[1] ?? time,
    seconds: match?.[2] ?? '',
    period: match?.[3] ?? '',
    date: new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }).format(date),
  };
};

const PaymentMetadata = ({
  type,
  paymentId,
  depositMetadata,
  withdrawMetadata,
  getDepositMetadata,
  getWithdrawalMetadata,
  depositMetadataIsLoaded,
  withdrawMetadataIsLoaded,
  theme,
}) => {
  const dynamicStyles = Object.keys(theme).length ? getDynamicStyles(theme) : ` `;
  const [selectedMetadata, setSelectedMetadata] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const handleOpenMetadata = data => {
    setSelectedMetadata(data);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedMetadata(null);
    setIsModalOpen(false);
  };

  const handleOpenCollapse = () => {
    if (type === 'deposit') {
      getDepositMetadata(paymentId);
    }
    if (type === 'withdraw') {
      getWithdrawalMetadata(paymentId);
    }
  };

  const handleCopyJson = async () => {
    if (!selectedMetadata) return;

    await navigator.clipboard.writeText(JSON.stringify(selectedMetadata, null, 2));

    setIsCopied(true);

    const timer = setTimeout(() => {
      setIsCopied(false);
    }, 2000);

    return () => clearTimeout(timer);
  };

  const dataIsLoaded = type === 'deposit' ? depositMetadataIsLoaded : withdrawMetadataIsLoaded;
  const metadata = type === 'deposit' ? depositMetadata : withdrawMetadata;

  return (
    <div className="PaymentMetadata">
      <Collapsible captionId="justMetadata" handleOpen={handleOpenCollapse}>
        {!dataIsLoaded && <TableLoader />}
        {dataIsLoaded && (
          <>
            {metadata && Object.keys(metadata).length > 0 ? (
              <div className="PaymentMetadata__list">
                {Object.entries(metadata).map(([date, data]) => {
                  const { hoursMinutes, seconds, period, date: formattedDate } = formatMetadataDate(date);

                  return (
                    <div key={date} className="PaymentMetadata__list-item">
                      <div className="PaymentMetadata__list-info">
                        <span className="PaymentMetadata__list-time">
                          {hoursMinutes}
                          <span className="PaymentMetadata__list-seconds">{seconds} </span>
                          <span className="PaymentMetadata__list-period">{period}</span>
                        </span>

                        <span className="PaymentMetadata__list-date">{formattedDate}</span>
                      </div>

                      <button
                        type="button"
                        className="PaymentMetadata__list-button"
                        onClick={() => handleOpenMetadata(data)}>
                        Check metadata
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <NoItemsPanel icon={noPaymentsImage} captionSlug="noData" />
            )}
          </>
        )}
      </Collapsible>

      <BasicModal captionId="justMetadata" isOpen={isModalOpen} onRequestClose={handleCloseModal}>
        {selectedMetadata && (
          <div className="PaymentMetadataModal">
            <div className="PaymentMetadataModal__info">{renderValue(selectedMetadata)}</div>
            <div className="PaymentMetadataModal__actions">
              <button type="button" className="PaymentMetadataModal__actions-copy" onClick={handleCopyJson}>
                {isCopied ? <CheckIcon /> : <CopyIcon />}
                <FormattedMessage id="justCopyJSON">{txt => <span>{txt}</span>}</FormattedMessage>
              </button>
              <DefaultButton
                textId="justClose"
                className="PaymentMetadataModal__actions-close"
                type="button"
                onClick={handleCloseModal}
              />
            </div>
          </div>
        )}
      </BasicModal>

      <style jsx global>
        {staticStyles}
      </style>
      <style jsx global>
        {dynamicStyles}
      </style>
    </div>
  );
};

PaymentMetadata.propTypes = {
  type: PropTypes.string.isRequired,
  paymentId: PropTypes.string.isRequired,
  depositMetadata: PropTypes.object.isRequired,
  withdrawMetadata: PropTypes.object.isRequired,
  getDepositMetadata: PropTypes.func.isRequired,
  getWithdrawalMetadata: PropTypes.func.isRequired,
  depositMetadataIsLoaded: PropTypes.bool.isRequired,
  withdrawMetadataIsLoaded: PropTypes.bool.isRequired,
  theme: PropTypes.object,
};

PaymentMetadata.defaultProps = {
  theme: {},
};

export default compose(
  withTheme(),
  connect(
    state => ({
      depositMetadataIsLoaded: state.backoffice.payments.depositMetadataIsLoaded,
      depositMetadata: state.backoffice.payments.depositMetadata,
      withdrawMetadataIsLoaded: state.backoffice.payments.withdrawMetadataIsLoaded,
      withdrawMetadata: state.backoffice.payments.withdrawMetadata,
    }),
    {
      getDepositMetadata: id => getDepositMetadata.request(id),
      getWithdrawalMetadata: id => getWithdrawalMetadata.request(id),
    }
  )
)(PaymentMetadata);
export { PaymentMetadata };
